/**
 * Report a user or a piece of content.
 *
 * App Store Guideline 1.2 requires an app carrying user-generated content to offer a way to report
 * offensive content AND a way to block the person behind it. Trade Loop already had blocking
 * (ProfilePreviewModal's menu, Settings > Blocked Users). This is the other half.
 *
 * The block option lives INSIDE the report rather than as a follow-up prompt: someone reporting
 * harassment nearly always wants the harasser gone too, and sending them off to find a second
 * screen after describing an unpleasant experience is the wrong thing to do. It is a checkbox, not
 * a default, because reporting spam is not a reason to sever a working partnership.
 */

import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/contexts/ToastContext";
import {
  REPORT_REASONS,
  useReportContent,
  type ReportContentType,
  type ReportReason,
} from "@/api/extensions/useReportContent";

/** Matches the server's cap so the counter never promises room the API will trim away. */
const DETAILS_MAX = 2000;

interface ReportContentModalProps {
  open: boolean;
  onClose: () => void;
  /** Who is being reported. Required — every report is ultimately about a person. */
  reportedUserId: string;
  reportedUserName?: string;
  /** What is being reported. Defaults to the person themselves. */
  contentType?: ReportContentType;
  /** The specific message/referral id, when reporting one piece of content rather than a person. */
  contentId?: string | null;
  /** Hidden when the pair are not connected — there is no relationship to sever. */
  canBlock?: boolean;
  /** Lets the caller close a parent sheet or refresh a thread after a successful report. */
  onReported?: (alsoBlocked: boolean) => void;
}

export function ReportContentModal({
  open,
  onClose,
  reportedUserId,
  reportedUserName,
  contentType = "user",
  contentId = null,
  canBlock = true,
  onReported,
}: ReportContentModalProps) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState("");
  const [alsoBlock, setAlsoBlock] = useState(false);
  const { showSuccess, showError } = useToast();
  const reportContent = useReportContent();

  const displayName = reportedUserName || "this user";
  // Mirrors the server rule: "Something else" with no explanation is unactionable.
  const detailsRequired = reason === "other";
  const canSubmit =
    reason !== null && (!detailsRequired || details.trim().length > 0) && !reportContent.isPending;

  const resetAndClose = () => {
    setReason(null);
    setDetails("");
    setAlsoBlock(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason) return;

    try {
      const result = await reportContent.mutateAsync({
        reported_user_id: reportedUserId,
        reason,
        content_type: contentType,
        content_id: contentId,
        details: details.trim() || null,
        also_block: canBlock && alsoBlock,
      });

      // A duplicate comes back as a success with its own wording — the report was already filed,
      // and telling the user it failed would only make them send it a third time.
      showSuccess(
        result.duplicate ? "Already Reported" : "Report Sent",
        result.data?.alsoBlocked
          ? `Our team will review this within 24 hours. ${displayName} has been blocked.`
          : result.message,
      );
      onReported?.(result.data?.alsoBlocked ?? false);
      resetAndClose();
    } catch (error: any) {
      showError(
        "Report Failed",
        error?.response?.data?.message || "We could not send that report. Please try again.",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && resetAndClose()}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Report {reportedUserName || "User"}
          </DialogTitle>
          <DialogDescription>
            Tell us what is wrong and our team will review it within 24 hours. Reports are
            confidential — {displayName} is not told who reported them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Why are you reporting this?</Label>
            <RadioGroup
              value={reason ?? ""}
              onValueChange={(value) => setReason(value as ReportReason)}
              className="gap-3"
            >
              {REPORT_REASONS.map((option) => (
                <div key={option.value} className="flex items-start gap-3">
                  <RadioGroupItem
                    value={option.value}
                    id={`report-reason-${option.value}`}
                    className="mt-1"
                  />
                  <Label
                    htmlFor={`report-reason-${option.value}`}
                    className="font-normal cursor-pointer leading-tight"
                  >
                    <span className="block text-sm text-gray-900">{option.label}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{option.hint}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="report-details" className="text-sm font-medium">
              Details {detailsRequired ? "(required)" : "(optional)"}
            </Label>
            <Textarea
              id="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value.slice(0, DETAILS_MAX))}
              placeholder="What happened? Anything you can add helps us act on this faster."
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-gray-400 text-right">
              {details.length}/{DETAILS_MAX}
            </p>
          </div>

          {canBlock && (
            <div className="flex items-start gap-3 rounded-md border border-gray-200 p-3">
              <Checkbox
                id="report-also-block"
                checked={alsoBlock}
                onCheckedChange={(checked) => setAlsoBlock(checked === true)}
                className="mt-0.5"
              />
              <Label
                htmlFor="report-also-block"
                className="font-normal cursor-pointer leading-tight"
              >
                <span className="block text-sm text-gray-900">
                  Also block {displayName}
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  They will not be able to message you or see your profile. You can undo this in
                  Settings.
                </span>
              </Label>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={resetAndClose} disabled={reportContent.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {reportContent.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Report"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReportContentModal;
